"use strict";
/**
 * SOLID Review
 * S — Strong. One concern: what a port PROMISES about the data crossing it, and the one function
 *     that holds an implementation to that promise. It knows no schema library and no transport.
 * O — Strong. A contract is an interface, so any schema library can implement it; the guard needs
 *     no change per port, per op or per library.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two interfaces and one function. A consumer that only wants to enumerate ops still sees
 *     the two parse methods, which is the cost of keeping "what is this port" in one object.
 * D — Strong. Depends on nothing. Zod lives behind `@ml3d/kernel/zod`; this file is what lets the
 *     frontend keep importing the kernel without acquiring a schema dependency.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NO_OBSERVER = void 0;
exports.portFailure = portFailure;
exports.guard = guard;
function portFailure(code, message) {
    return { success: false, error: { code, message } };
}
exports.NO_OBSERVER = {
    onCall: () => undefined,
    onResult: () => undefined,
};
const CODE_CONTRACT = 'CONTRACT';
const CODE_INTERNAL = 'INTERNAL';
const CODE_DEADLINE = 'DEADLINE';
function messageOf(err) {
    return err instanceof Error ? err.message : String(err);
}
function isCallContext(value) {
    return (typeof value === 'object' && value !== null &&
        typeof value.requestId === 'string' &&
        typeof value.deadlineAt === 'number');
}
/**
 * Hold `impl` to `contract`: every op's arguments are parsed before the call and its result after.
 *
 * WHAT A NON-LOCAL OP NEVER DOES IS THROW ACROSS THE BOUNDARY. Three things become a failed
 * Result instead:
 *   - the caller's arguments violate the contract       → `CONTRACT` (across a transport this is
 *                                                          the server refusing the message)
 *   - the provider's result violates the contract       → `CONTRACT` (the provider is at fault, and
 *                                                          the observer sees `ok: false`)
 *   - the provider throws                                → `INTERNAL`
 * and a call made after its own deadline is refused with `DEADLINE` without reaching the provider.
 * A caller that already speaks Result handles all four the way it handles any other failure.
 *
 * A LOCAL op is a platform service in the same process: it is parsed both ways too, but it stays
 * synchronous and its throws propagate — there is no boundary to carry a failure envelope across.
 * A STREAM op has its arguments parsed and its iterator passed through untouched.
 *
 * Property reads that are not ops resolve to `undefined` rather than throwing: `await`ing the proxy
 * probes `then`, `JSON.stringify` probes `toJSON`, and the type system already forbids calling an
 * op the contract does not list. The contract, not the proxy, is what refuses an unknown op — at
 * `parseArgs`, by name.
 */
function guard(impl, contract, observer) {
    const ops = new Set(contract.ops);
    const cache = new Map();
    const call = (op) => {
        const target = impl[op];
        if (typeof target !== 'function') {
            throw new Error(`Port '${contract.name}' promises op '${op}' but its provider has no such method.`);
        }
        const method = target;
        if (contract.local) {
            return (...args) => {
                const handle = observer.onCall(contract.name, op, null);
                const started = Date.now();
                try {
                    const result = contract.parseResult(op, method.apply(impl, contract.parseArgs(op, args)));
                    observer.onResult(contract.name, op, null, true, Date.now() - started, handle);
                    return result;
                }
                catch (err) {
                    observer.onResult(contract.name, op, null, false, Date.now() - started, handle);
                    throw err;
                }
            };
        }
        if (contract.stream) {
            return (...args) => {
                const ctx = isCallContext(args[0]) ? args[0] : null;
                const handle = observer.onCall(contract.name, op, ctx);
                const started = Date.now();
                // ⚠ THIS ARM REPORTED THE CALL AND NEVER ITS OUTCOME, so an observer that opens something at
                // `onCall` was never told to close it — one leak per subscribe, on the one stream port.
                // A stream's WORK outlives this call, so what is reported is the handover: the subscription
                // was established, or it was refused. The iterator itself is still passed through untouched.
                let parsed;
                try {
                    parsed = contract.parseArgs(op, args);
                }
                catch (err) {
                    observer.onResult(contract.name, op, ctx, false, Date.now() - started, handle, CODE_CONTRACT);
                    throw err;
                }
                try {
                    const iterator = method.apply(impl, parsed);
                    observer.onResult(contract.name, op, ctx, true, Date.now() - started, handle);
                    return iterator;
                }
                catch (err) {
                    observer.onResult(contract.name, op, ctx, false, Date.now() - started, handle, CODE_INTERNAL);
                    throw err;
                }
            };
        }
        return async (...args) => {
            const ctx = isCallContext(args[0]) ? args[0] : null;
            const handle = observer.onCall(contract.name, op, ctx);
            const started = Date.now();
            const finish = (ok, code) => observer.onResult(contract.name, op, ctx, ok, Date.now() - started, handle, code);
            if (ctx !== null && started > ctx.deadlineAt) {
                finish(false, CODE_DEADLINE);
                return portFailure(CODE_DEADLINE, `Port '${contract.name}.${op}' called after its deadline.`);
            }
            let parsedArgs;
            try {
                parsedArgs = contract.parseArgs(op, args);
            }
            catch (err) {
                finish(false, CODE_CONTRACT);
                return portFailure(CODE_CONTRACT, `Port '${contract.name}.${op}' refused its arguments: ${messageOf(err)}`);
            }
            let raw;
            try {
                raw = await method.apply(impl, parsedArgs);
            }
            catch (err) {
                finish(false, CODE_INTERNAL);
                return portFailure(CODE_INTERNAL, `Port '${contract.name}.${op}' threw: ${messageOf(err)}`);
            }
            try {
                const result = contract.parseResult(op, raw);
                finish(true);
                return result;
            }
            catch (err) {
                finish(false, CODE_CONTRACT);
                return portFailure(CODE_CONTRACT, `Port '${contract.name}.${op}' returned off-contract data: ${messageOf(err)}`);
            }
        };
    };
    return new Proxy(impl, {
        get(_target, prop) {
            if (typeof prop !== 'string' || !ops.has(prop))
                return undefined;
            let fn = cache.get(prop);
            if (fn === undefined) {
                fn = call(prop);
                cache.set(prop, fn);
            }
            return fn;
        },
    });
}
//# sourceMappingURL=PortContract.js.map